// System dialogs (ui-flow §9): modal, a single primary button, our own copy.
//
// Implementer: client UI. Consumer: app.ts (maps Connector `gaveUp` reasons and connection loss
// to these ids).
//
// One system dialog is shown at a time: `showDialog` replaces the one that is open. System
// dialogs are not closable with Esc or the backdrop; their primary button closes them and then
// runs `onPrimary`. `outdated` reloads the page when no `onPrimary` is given.

import { h } from '../dom.js';
import { hasKey, t } from '../i18n/t.js';
import { type ModalHandle, openModal } from './modal.js';

export const DIALOG_IDS = [
  'connecting',
  'cant_reach',
  'connection_lost',
  'money_connection_lost',
  'room_full',
  'outdated',
  'kicked',
  'too_many',
] as const;
export type DialogId = (typeof DIALOG_IDS)[number];

export interface DialogOptions {
  /** The primary button (Retry, Reconnect, Try again, Refresh, OK). */
  readonly onPrimary?: () => void;
}

/** What a system dialog says (DOM-free; ui-flow §9 copy). */
export interface DialogCopy {
  readonly title: string;
  /** `""` when the dialog has no body line. */
  readonly body: string;
  /** Primary button label, or null for the button-less `connecting` dialog. */
  readonly primary: string | null;
  /** Shows the spinner (`connecting`). */
  readonly spinner: boolean;
}

export function dialogCopy(id: DialogId): DialogCopy {
  const body = `dialog.${id}.body`;
  const button = `dialog.${id}.button`;
  return {
    title: t(`dialog.${id}.title`),
    body: hasKey(body) ? t(body) : '',
    primary: hasKey(button) ? t(button) : null,
    spinner: id === 'connecting',
  };
}

let current: { readonly id: DialogId; readonly handle: ModalHandle } | null = null;

/** The click sound for dialog buttons (audio §3 row 7); app.ts sets it at boot. */
let clickHook: (() => void) | null = null;
export function setDialogClick(fn: (() => void) | null): void {
  clickHook = fn;
}

/** Opens (or replaces) the system dialog `id`. */
export function showDialog(id: DialogId, opts: DialogOptions = {}): ModalHandle {
  closeDialog();
  const copy = dialogCopy(id);
  const titleId = `dialog-${id}-title`;
  const primary =
    copy.primary === null
      ? null
      : h(
          'button',
          {
            type: 'button',
            class: id === 'connection_lost' ? 'btn btn-go' : 'btn btn-act',
            onClick: () => {
              clickHook?.();
              handle.close();
              if (opts.onPrimary) opts.onPrimary();
              else if (id === 'outdated') globalThis.location?.reload();
            },
          },
          copy.primary,
        );
  const content = h(
    'div',
    { class: `dialog dialog-${id}`, 'aria-labelledby': titleId },
    h('h2', { class: 'dialog-title', id: titleId }, copy.title),
    h(
      'div',
      { class: 'dialog-panel', 'aria-hidden': 'true' },
      copy.spinner
        ? h('span', { class: 'ring-spinner ring-spinner-lg' })
        : h('span', { class: 'dialog-reconnect' }, '⟳'),
    ),
    copy.body !== '' ? h('p', { class: 'dialog-body' }, copy.body) : null,
    primary ? h('div', { class: 'dialog-actions' }, primary) : null,
  );
  const handle = openModal(content, {
    closable: false,
    label: copy.title,
    className: 'modal-dialog',
    onClose: () => {
      if (current?.handle === handle) current = null;
    },
  });
  current = { id, handle };
  return handle;
}

/** Closes the open system dialog (only if it is `id`, when given). */
export function closeDialog(id?: DialogId): void {
  if (!current || (id !== undefined && current.id !== id)) return;
  const { handle } = current;
  current = null;
  handle.close();
}

/** The system dialog that is open, if any. */
export function openDialogId(): DialogId | null {
  return current?.id ?? null;
}

/** Generic confirm (ui-flow §9 `confirm`): resolves true for the primary button. */
export function confirmDialog(
  title: string,
  body: string,
  primary: string,
  cancel: string,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let answered = false;
    const answer = (yes: boolean): void => {
      if (answered) return;
      answered = true;
      resolve(yes);
    };
    const titleId = 'dialog-confirm-title';
    const content = h(
      'div',
      { class: 'dialog dialog-confirm', 'aria-labelledby': titleId },
      h('h2', { class: 'dialog-title', id: titleId }, title),
      body !== '' ? h('p', { class: 'dialog-body' }, body) : null,
      h(
        'div',
        { class: 'dialog-actions' },
        h(
          'button',
          {
            type: 'button',
            class: 'btn btn-quiet',
            onClick: () => {
              clickHook?.();
              answer(false);
              handle.close();
            },
          },
          cancel,
        ),
        h(
          'button',
          {
            type: 'button',
            class: 'btn btn-act',
            onClick: () => {
              clickHook?.();
              answer(true);
              handle.close();
            },
          },
          primary,
        ),
      ),
    );
    // Esc, the backdrop and ✕ count as Cancel.
    const handle = openModal(content, {
      closable: true,
      label: title,
      onClose: () => answer(false),
    });
  });
}

/**
 * A one-button notice dialog (money refusals, ERROR 11–16 and buy-in failures): closable with
 * ×, Esc or the backdrop; resolves when it closes.
 */
export function messageDialog(title: string, body: string, button: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const titleId = 'dialog-message-title';
    const content = h(
      'div',
      { class: 'dialog dialog-message', 'aria-labelledby': titleId },
      h('h2', { class: 'dialog-title', id: titleId }, title),
      body !== '' ? h('p', { class: 'dialog-body' }, body) : null,
      h(
        'div',
        { class: 'dialog-actions' },
        h(
          'button',
          {
            type: 'button',
            class: 'btn btn-act',
            onClick: () => {
              clickHook?.();
              handle.close();
            },
          },
          button,
        ),
      ),
    );
    const handle = openModal(content, {
      closable: true,
      label: title,
      className: 'modal-message',
      onCloseClick: () => clickHook?.(),
      onClose: () => resolve(),
    });
  });
}
